import { callPeerName, callPeerNumber } from "@dialstack/sdk-react/softphone";
import { ReactNode } from "react";

import Box from "@mui/material/Box";

import { popCallerName, popCallerNumber } from "../incoming-call-pop.slice";
import { isColleagueLine } from "../phone-match.util";
import useUnknownCallerActions from "../unknown-caller/use-unknown-caller-actions.hook";
import ColleagueCallCard from "./colleague-call-card.component";
import {
  callCardSx,
  softphoneDialogSx
} from "./dialstack-softphone-widget.styles";
import IncomingCallUnknownCallerCard, {
  UnknownCaller
} from "./incoming-call-unknown-caller-card.component";
import useColleagueLookup from "./use-colleague-lookup.hook";
import useInboundCallPops, {
  InboundCallMatch,
  inboundCallLabel
} from "./use-inbound-call-pops.hook";
import useRecentCaller from "./use-recent-caller.hook";

// The number of whoever is on the line — on a transferred leg that is the
// original caller, not the colleague's extension the leg was placed from.
const callerNumber = ({ call, pop }: InboundCallMatch) =>
  pop ? popCallerNumber(pop) : callPeerNumber(call);

const toCaller = (match: InboundCallMatch): UnknownCaller => ({
  backendCallId: match.backendCallId,
  remoteNumber: callerNumber(match),
  fromName: match.pop ? popCallerName(match.pop) : callPeerName(match.call),
  contact: match.pop?.contact ?? null,
  clinicId: match.pop?.clinicId ?? null,
  transferredBy: match.pop?.transfer
    ? (match.pop.transfer.byName ?? "a colleague")
    : null
});

const isRingingIn = (match: InboundCallMatch) =>
  inboundCallLabel(match.call) === "Incoming call";

// The unknown-caller card and its dialogs for the softphone widget. Returned
// as two nodes because they must live in different places: the card sits in
// the panel with the patient card, while the dialogs must be rendered OUTSIDE
// the panel's Paper — its onMouseDown/onKeyDown handlers steal focus into the
// dial field — and must outlive the card, which unmounts on hangup.
//
// `cardCallIds` narrows the cards to those calls: the unfolded pop shows the
// one call that is ringing, every other call being folded to its bar
// (ENG-2226). The dialogs and actions are unaffected: a ringing call sorts
// first either way, so they follow the same caller.
export default function useSoftphoneUnknownCaller({
  cardCallIds
}: { cardCallIds?: string[] } = {}): {
  card: ReactNode;
  dialogs: ReactNode;
  // The card is for a call that is still ringing, so it belongs above the
  // cards of calls already in progress.
  isRinging: boolean;
} {
  const allMatches = useInboundCallPops();
  const matches =
    cardCallIds === undefined
      ? allMatches
      : allMatches.filter((match) => cardCallIds.includes(match.callId));
  // Any live inbound call the backend did not match to a patient — including
  // one whose pop has not arrived (actions wait for the backend id).
  const unmatched = matches.filter(
    (match) => !match.pop || match.pop.patientIds.length === 0
  );
  // A colleague ringing from their extension or their web softphone is not an
  // unknown caller: there is nobody to Quick add, attach or save, and nothing
  // to keep once it ends.
  const colleagueCalls = unmatched.filter((match) =>
    isColleagueLine(callerNumber(match))
  );
  const liveUnknown = unmatched.filter(
    (match) => !isColleagueLine(callerNumber(match))
  );
  const lookupColleague = useColleagueLookup(colleagueCalls.map(callerNumber));
  const { recentCaller, dismissRecentCaller } = useRecentCaller(liveUnknown);

  // Ringing calls sort first, so the actionable card follows the call that is
  // waiting on a decision; any other unknown caller still gets a labelled
  // card, so every live call is accounted for in the panel (ENG-2012).
  const [live, ...otherLive] = liveUnknown;
  // The recent-caller card (a call that just ended) belongs to the idle
  // panel, never inside another call's pop.
  const caller: UnknownCaller | null = live
    ? toCaller(live)
    : cardCallIds === undefined
      ? recentCaller
      : null;

  const { actions, dialogs } = useUnknownCallerActions({
    callId: caller?.backendCallId ?? null,
    remoteNumber: caller?.remoteNumber ?? "",
    fromName: caller?.fromName ?? null,
    clinicId: caller?.clinicId ?? null,
    dialogSx: softphoneDialogSx
  });

  const unknownCards = caller && (
    <>
      <Box sx={callCardSx}>
        <IncomingCallUnknownCallerCard
          caller={caller}
          liveLabel={live ? inboundCallLabel(live.call) : null}
          actions={actions}
          onDismiss={dismissRecentCaller}
          inPop={cardCallIds !== undefined}
        />
      </Box>
      {otherLive.map((match) => (
        <Box key={match.callId} sx={callCardSx}>
          <IncomingCallUnknownCallerCard
            caller={toCaller(match)}
            liveLabel={inboundCallLabel(match.call)}
            inPop={cardCallIds !== undefined}
          />
        </Box>
      ))}
    </>
  );

  const card =
    colleagueCalls.length > 0 || unknownCards ? (
      <>
        {colleagueCalls.map((match) => {
          const colleague = lookupColleague(
            callerNumber(match),
            match.pop?.fromName ?? callPeerName(match.call)
          );
          return (
            colleague && (
              <ColleagueCallCard
                key={match.callId}
                caption={
                  match.pop?.transfer ? "Transferred call" : "Internal call"
                }
                colleague={colleague}
              />
            )
          );
        })}
        {unknownCards}
      </>
    ) : null;

  return { card, dialogs, isRinging: unmatched.some(isRingingIn) };
}
